import { Controller } from "@hotwired/stimulus"

const LOCATION_KEY = "field_user_location"

// Connects to data-controller="place-sort"
// Manages sort state and localStorage persistence.
// Open/close is delegated to the bottom-sheet controller on the same element.
//
// Used by the Places list (defaults) and the place page's mission list
// (card #4098), which sets its own storage key, labels and forms, and in the
// offline shell sets local so nothing goes to the network.
export default class extends Controller {
  static targets = ["label", "sortByInput", "sortDirInput"]
  // locationSorts: sort keys that are ordered by distance and so need the
  // device fix sent as lat/lng (e.g. ["distance"]). Empty for sorts that don't.
  // labels: overrides the label shown for each sort key. When given, a saved
  // choice whose key isn't in it is ignored.
  // storageKey: the localStorage key the choice is saved under.
  // local: sort without the network. Apply saves the choice and dispatches
  // place-sort:applied for the page to re-sort itself; the frame is untouched.
  // forms: CSS selector for forms that carry sort_by/sort_dir fields (kept in
  // step with the choice) and the q search text (kept when the sort changes).
  static values  = {
    sortBy: String,
    sortDir: String,
    frame: String,
    locationSorts: { type: Array, default: [] },
    labels: { type: Object, default: {} },
    storageKey: { type: String, default: "field_places_sort" },
    local: { type: Boolean, default: false },
    forms: { type: String, default: "" }
  }

  connect() {
    const saved = this.loadPreference()
    if (saved && (saved.sortBy !== this.sortByValue || saved.sortDir !== this.sortDirValue)) {
      this.sortByValue  = saved.sortBy
      this.sortDirValue = saved.sortDir
      if (this.localValue) {
        this.render()
      } else {
        this.applySort()
      }
      return
    }
    this.render()
  }

  apply() {
    this.sortByValue  = this.sortByInputTargets.find(el => el.checked)?.value  || this.sortByValue
    this.sortDirValue = this.sortDirInputTargets.find(el => el.checked)?.value || this.sortDirValue
    this.savePreference()
    // Close the bottom-sheet controller that shares this element
    const sheet = this.application.getControllerForElementAndIdentifier(this.element, "bottom-sheet")
    if (sheet) sheet.close()

    if (this.localValue) {
      this.render()
    } else {
      this.applySort()
    }
    this.dispatch("applied", { detail: { sortBy: this.sortByValue, sortDir: this.sortDirValue } })
  }

  applySort() {
    this.writeFormFields()

    const frame = document.getElementById(this.frameValue)
    if (!frame) {
      this.render()
      return
    }

    const url = new URL(window.location.href)
    url.searchParams.set("sort_by",  this.sortByValue)
    url.searchParams.set("sort_dir", this.sortDirValue)
    this.applyLocation(url)
    this.applySearchQuery(url)

    frame.src = url.toString()
    this.render()
  }

  // Sends the stored device fix for location-based sorts; clears any stale
  // fix otherwise. No stored fix means no params, and the server falls back
  // to its default order.
  applyLocation(url) {
    url.searchParams.delete("lat")
    url.searchParams.delete("lng")
    if (!this.locationSortsValue.includes(this.sortByValue)) return

    const fix = this.loadLocation()
    if (!Number.isFinite(fix?.lat) || !Number.isFinite(fix?.lng)) return

    url.searchParams.set("lat", fix.lat)
    url.searchParams.set("lng", fix.lng)
  }

  // A search submitted into the frame never reaches window.location, so the
  // current text is read from the search form instead.
  applySearchQuery(url) {
    const queryField = this.forms().map(form => form.elements.namedItem("q")).find(Boolean)
    if (!queryField) return

    if (queryField.value) {
      url.searchParams.set("q", queryField.value)
    } else {
      url.searchParams.delete("q")
    }
  }

  // Keeps each form's hidden sort fields on the current choice, so a search
  // or a schedule submitted after Apply keeps the order.
  writeFormFields() {
    this.forms().forEach(form => {
      const sortBy  = form.elements.namedItem("sort_by")
      const sortDir = form.elements.namedItem("sort_dir")
      if (sortBy)  sortBy.value  = this.sortByValue
      if (sortDir) sortDir.value = this.sortDirValue
    })
  }

  forms() {
    if (!this.formsValue) return []
    return Array.from(document.querySelectorAll(this.formsValue))
  }

  loadLocation() {
    try { return JSON.parse(localStorage.getItem(LOCATION_KEY)) }
    catch { return null }
  }

  render() {
    this.updateLabel()
    this.syncInputs()
  }

  updateLabel() {
    if (!this.hasLabelTarget) return

    const defaults = { name: "Name", visit_frequency: "Visit Freq", priority: "Priority", schedule_at: "Next Scheduled", distance: "Nearest" }
    const labels   = { ...defaults, ...this.labelsValue }
    const arrow  = this.sortDirValue === "desc" ? "↓" : "↑"
    this.labelTarget.textContent = `Sort: ${labels[this.sortByValue] || "Name"} ${arrow}`
  }

  // The sheet sits outside the frame, so after a restore its radios would
  // otherwise still show what the server first rendered.
  syncInputs() {
    this.sortByInputTargets.forEach(el => { el.checked = el.value === this.sortByValue })
    this.sortDirInputTargets.forEach(el => { el.checked = el.value === this.sortDirValue })
  }

  savePreference() {
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify({ sortBy: this.sortByValue, sortDir: this.sortDirValue }))
    } catch {
      // Private window or storage full: the choice still applies, it just isn't remembered.
    }
  }

  loadPreference() {
    let saved
    try { saved = JSON.parse(localStorage.getItem(this.storageKeyValue)) }
    catch { return null }

    if (!saved?.sortBy || !["asc", "desc"].includes(saved.sortDir)) return null
    const allowed = Object.keys(this.labelsValue)
    if (allowed.length > 0 && !allowed.includes(saved.sortBy)) return null
    return saved
  }
}
